import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Profile } from '../../../models/profile.model';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { AmountUtils } from '../../../../services/utils/format-amounts.utils';
import { Wallet } from '../../../models/wallet.model';
import { BillingEntry, Invoice } from '../../../models/invoices.model';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormGroup } from '@angular/forms';
import { PaymentMethod } from '../../../models/partner.model';
import { WalletService, PaymentIntentResponse } from '../../../../services/wallet.service';
import { NotifificationService } from '../../../../services/notification.service';
import { StripeService } from '../../../../services/stripe.service';
import { WebSocketService } from '../../../../services/websocket.service';
import { NotificationType } from '../../../../model/notification.model';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ProfileService } from '../../../auth/services/profile.service';

@Component({
  selector: 'app-wallet-offcanvas',
  templateUrl: './wallet-offcanvas.component.html',
  styleUrls: ['./wallet-offcanvas.component.css'],
  standalone: false
})
export class WalletOffcanvasComponent implements OnInit, OnDestroy {
  @Input() profile?: Profile;
  @Input() payInvoiceId?: number;
  @ViewChild('stripePaymentElement', { static: false }) stripeElementRef?: ElementRef<HTMLDivElement>;

  AmountUtils = AmountUtils;
  fields?: FormlyFieldConfig[];
  form = new FormGroup({});
  model?: { paymentMethod?: PaymentMethod; };

  detailsOpen = false;
  isLoading = false;
  paymentReady = false;
  /** False finché i pagamenti online sono spenti (Stripe disattivato in attesa di POK). */
  paymentsEnabled = true;
  paymentError?: string;
  paymentSuccess = false;

  /** Voce in pagamento: una fattura o il residuo non fatturato. */
  payingLabel?: string;
  payingAmount?: number;

  private wsSub?: Subscription;

  constructor(
    private activeOffcanvas: NgbActiveOffcanvas,
    private walletService: WalletService,
    private notificationService: NotifificationService,
    private stripeService: StripeService,
    private cdr: ChangeDetectorRef,
    private wsService: WebSocketService,
    protected profileService: ProfileService
  ) { }

  async ngOnInit() {
    this.initForm();
    this.model = {};
    // Ascolta notifiche BILLING via WebSocket → aggiorna wallet in realtime
    this.wsSub = this.wsService.notifications$.pipe(
      filter(n => n?.message === NotificationType.BILLING)
    ).subscribe(() => {
      this.walletService.getWallet().then(() => this.cdr.detectChanges());
    });
    this.paymentsEnabled = await this.walletService.isPaymentsEnabled();
    this.cdr.detectChanges();
    if (this.payInvoiceId && this.paymentsEnabled) {
      await this.walletService.getWallet();
      const invoice = this.unpaidInvoices.find(i => i.id === this.payInvoiceId);
      if (invoice) await this.payInvoice(invoice);
    }
  }

  ngOnDestroy() {
    this.stripeService.destroy();
    this.wsSub?.unsubscribe();
  }

  initForm() {
    this.fields = [
      {
        key: 'paymentMethod',
        type: 'paymentMethod',
        props: {
          required: true,
        },
      }
    ];
  }

  closeOffcanvas() {
    this.activeOffcanvas.dismiss();
  }

  toggleDetails() {
    this.detailsOpen = !this.detailsOpen;
  }

  get unpaidInvoices(): Invoice[] {
    return this.wallet?.unpaidInvoices ?? [];
  }

  async payInvoice(invoice: Invoice) {
    await this.openPayment(`Invoice ${invoice.number}`, () => this.walletService.payInvoice(invoice.id));
  }

  async payPending() {
    await this.openPayment('Charges not yet invoiced', () => this.walletService.payPending());
  }

  /**
   * Avvia il pagamento della voce selezionata: con POK si va alla pagina di pagamento ospitata
   * (il ritorno è gestito dalla pagina fatture), con Stripe si monta il Payment Element.
   */
  private async openPayment(label: string, start: () => Promise<PaymentIntentResponse>) {
    if (this.isLoading) return;
    this.isLoading = true;
    this.paymentError = undefined;
    try {
      const { clientSecret, amount, provider, redirectUrl } = await start();
      if (provider === 'POK') {
        if (!redirectUrl) throw new Error('Payment page not available');
        window.location.href = redirectUrl;
        return;
      }
      this.payingLabel = label;
      this.payingAmount = amount;
      this.paymentReady = true;
      this.cdr.detectChanges();
      const container = this.stripeElementRef?.nativeElement;
      if (!container) throw new Error('Stripe container not found in the DOM');
      await this.stripeService.init(clientSecret, container);
    } catch (error: any) {
      console.error('Payment initialization error', error);
      this.paymentError = error?.error?.message ?? error?.message ?? 'An error occurred while preparing the payment.';
      this.paymentReady = false;
      // pay-pending may have issued the invoice even if Stripe failed: refresh the list
      try {
        await this.walletService.getWallet();
      } catch (refreshError) {
        console.error('Wallet refresh error', refreshError);
      }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Step 2 — Conferma il pagamento tramite Stripe.js.
   */
  cancelPayment() {
    if (this.isLoading) return;
    this.stripeService.destroy();
    this.paymentReady = false;
    this.paymentError = undefined;
    this.payingLabel = undefined;
    this.payingAmount = undefined;
  }

  onBackdropClick(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('payment-modal-backdrop')) {
      this.cancelPayment();
    }
  }

  async confirmPayment() {
    if (this.isLoading) return;
    this.isLoading = true;
    this.paymentError = undefined;

    try {
      const result = await this.stripeService.confirmPayment();
      if (result.error) {
        this.paymentError = result.error.message;
      } else {
        this.paymentSuccess = true;
        this.notificationService.alertNotificationsService.success('Payment completed successfully!');
        await this.walletService.getWallet();
        setTimeout(() => {
          this.closeOffcanvas();
          this.payingLabel = undefined;
          this.payingAmount = undefined;
        }, 2000);
      }
    } catch (error: any) {
      console.error('Payment confirmation error', error);
      this.paymentError = error?.message ?? 'Payment failed. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  get wallet(): Wallet | undefined {
    return this.walletService.wallet;
  }

  /** Voci non ancora fatturate che compongono l'importo corrente. */
  get pendingEntries(): BillingEntry[] {
    return this.wallet?.pendingEntries ?? [];
  }

  getAmountClass(): string {
    return AmountUtils.usageClass(this.wallet?.unpaidTotal, this.wallet?.threshold);
  }


  /**
   * Etichetta per tipo di addebito, decisa a display-time così vale anche per le righe vecchie
   * (la description salvata in DB era "Quote request" / "Quote request acceptance").
   */
  entryLabel(entry: BillingEntry): string {
    switch ((entry.type || '').toUpperCase()) {
      case 'QUOTE_REQUEST':
        return 'Quote Interest Unlock';
      case 'QUOTE_REQUEST_ACCEPTANCE':
        return 'Additional Quote Request';
      default:
        return entry.description || entry.type;
    }
  }
}
